<template>
  <div>
    <div :style="{ backgroundImage: 'url(' + require('@/assets/images/bg-category.jpg') + ')' }"
         class="background expend-over-parent">
      <div class="container">
        <div class="col col-12 col-sm-6 d-flex flex-wrap justify-space-between">

          <h1 class="jumbo-giant-text white--text">Explore Outbrain network data</h1>
          <div class="jumbo-desc">{{$t('insight-desc')}}
          </div>
        </div>
      </div>
    </div>
    <filters-menu v-if="filters && filterOptions" :filter-options="filterOptions" :initial-filters="filters"
                  @filterChange="onChange">
    </filters-menu>
      <h2 v-if="loading" class="text-center mb-10"> Loading...</h2>
      <h2 class="text-center mb-10" v-else-if="!performanceByDeviceResults.length && !popularCategoriesResults.length &&!consumerInterestsResults.length && !adContentResults.length">The volume of campaign activity for this region and vertical does not meet minimum requirements to show charts.</h2>
    <div class="verticals">
      <verticals-section name="content" :dataItems="popularCategoriesResults" :filters="filters" class="vertical-section"
                         id="popular-categories"
                         title="Popular Categories" desc="See which publisher sections and categories get the highest CTRs" v-if="popularCategoriesResults.length"></verticals-section>
      <verticals-section name="consumer" :dataItems="consumerInterestsResults" :filters="filters" class="vertical-section"
                         id="consumer-interests"
                         title="Consumer Interests" desc="Explore the top-performing topics per region and vertical" v-if="consumerInterestsResults.length"></verticals-section>
      <verticals-section name="platform" :dataItems="performanceByDeviceResults" :filters="filters" class="vertical-section"
                         id="performance-by-device"
                         title="Performance By Device" desc="View user behavior and performance for mobile, tablet, and desktop" v-if="performanceByDeviceResults.length"></verticals-section>
      <verticals-section name="creative" :dataItems="adContentResults" :filters="filters" class="vertical-section"
                         id="ad-content"
                         title="Ad Content" desc="Discover the latest performance trends for headlines, images, and more" v-if="adContentResults.length"></verticals-section>

      <div class="campaign">
        <create-campaign></create-campaign>
      </div>

    </div>


  </div>
</template>

<script lang="ts">
import {Component} from 'vue-property-decorator';
import BaseComponent from "@/components/base/BaseComponent";
import {
  getConsumerInterestsResults,
  getPopularCategoriesResults,
  getAdContentResults,
  getPerformanceByDevice
} from '@/repositories/chartsRepository';
import {FilterItem, Filters, FiltersOptions, NamedUrl} from "@/models/Filters";
import FiltersMenu from "@/components/pages/category-insights/verticals/FiltersMenu.vue";
import {DataItem, DataSet} from "@/models/general";
import {brainpowerClient} from "@/repositories/Repositories";
import VerticalsSection from "./verticals/VerticalsSection.vue";
import CreateCampaign from "@/components/pages/category-insights/verticals/CreateCampaign.vue";
import {Period} from "@/models/response/BrainpowerApiResponse";
import {FILTERS} from "@/consts/consts";
import * as pattern from 'patternomaly'
import {getFilterCodesFromUrl} from "@/components/utils/utils";


@Component({
        components: {VerticalsSection, FiltersMenu, CreateCampaign}
    })
    export default class CategoryInsights extends BaseComponent {
        private filterOptions: FiltersOptions = {categories: [], regions: [],periods: []} as FiltersOptions;
        private filters: Filters = null
        private performanceByDeviceResults: DataItem[] = []
        private popularCategoriesResults: DataItem[] = []
        private consumerInterestsResults: DataItem[] = []
        private adContentResults: DataItem[] = []


        private dataFetchers = [
          {dataKey: 'performanceByDeviceResults', fetcher: getPerformanceByDevice},
          {dataKey: 'popularCategoriesResults', fetcher: getPopularCategoriesResults},
          {dataKey: 'consumerInterestsResults', fetcher: getConsumerInterestsResults},
          {dataKey: 'adContentResults', fetcher: getAdContentResults}
        ]

      mounted() {

            this.initializeFilter().then(() => {
                this.fetchData()
            })
        }

        async initializeFilter(): Promise<void> {
            let results: any = {};
            await Promise.allSettled(FILTERS.map((u: NamedUrl) =>
                    brainpowerClient.get(u.url, {params:u.params})
                        .then((res) => {
                                const result = res.data[u.name]
                                if (!result) {
                                    console.warn('received empty result', u)  //TODO log errors to server/track js. show error message to user, consider setting a default value
                                }
                                results[u.name] = result
                            }
                        )
                )
            )
                .catch(err => console.warn(err))
                .finally(() => {
                    this.filterOptions = {
                        categories: results.categories.filter((value: FilterItem) => value.active == true),
                        regions: results.regions.filter((value: FilterItem) => value.active == true),
                        // Ilan requirement BA-460
                        periods: results.periods.map((value:Period,index:any)=> {return{
                          active:index!==results.periods.length-1,
                          id:index,
                          code:value.startDate,
                          name:value.logicalName,
                          compareDisabled:value.compareDisabled}})
                    };
                    const codes = getFilterCodesFromUrl()
                    this.filters = {
                        category: this.filterOptions.categories.find(c => c.code === codes.category),
                        region: this.filterOptions.regions.find(r => r.code === codes.region),
                        period: this.filterOptions.periods.find(p => p.code == codes.period) || this.filterOptions.periods[0],
                        includeWoWValues: codes.includeWoWValues,
                        prevPeriod : codes.prevPeriod
                    };
                })
        }

  fetchData(){
          this.dataFetchers.forEach(mapping => this.fetchResults(mapping.dataKey, mapping.fetcher))
  }
        onChange(data: Filters) {

          this.filters = data;

          if(this.filters.includeWoWValues){
            this.filters.prevPeriod = this.filterOptions.periods[this.filters.period.id+1].code.toString();
          }
            this.fetchData()
        }

        fetchResults(resultsKey: string, fetchFunction: Function) {
            this.loading = true;
          (this as any)[resultsKey]  = []
          fetchFunction(this.filters).then((res: DataItem[]) => {
            (this as any)[resultsKey] = res;
          }).catch((error: Error) => {
                console.error(error);
          }).finally(() => this.loading = false);
        }

    }
</script>

<style lang="scss" scoped>
  .background {

    /* Set a specific height */
    height: 348px;

    /* Create the parallax scrolling effect */
    background-attachment: fixed;
    background-position: center top;
    background-repeat: no-repeat;
  }

  .vertical-section{
    background: #f7f7f7;
    border-radius: 8px;
    margin-bottom: 100px;
  }

  .jumbo-desc {
    height: 68px;
    width: 602px;
    color: #FFFFFF;
    font-size: 20px;
    line-height: 34px;
  }

  .v-select {
    margin: 10px;
  }

  .verticals{
    margin-bottom: 200px;
  }

  .campaign{
    background-image: url("../../../assets/images/bp-bg-banner.png");
    height: 338px;
  }
</style>


